import {
    categoryTarget,
    channelTarget,
    finalMonthPct,
    monthlyTarget,
    subCatTarget,
} from "../consts/demandplanning";

export function normalizePlanningData(raw) {
    if (raw == null) return {};
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
            return {};
        }
    }
    return typeof raw === "object" ? raw : {};
}

function isForecastYearMonthClosedForPersist(forecastYear, monthIndex0to11) {
    const fy =
        forecastYear != null && forecastYear !== ""
            ? Number.parseInt(String(forecastYear), 10)
            : NaN;
    if (!Number.isFinite(fy) || monthIndex0to11 < 0 || monthIndex0to11 > 11) return false;
    const now = new Date();
    const cy = now.getFullYear();
    const cm = now.getMonth();
    if (fy < cy) return true;
    if (fy > cy) return false;
    return monthIndex0to11 < cm;
}

function computeYtdForecastTotalForPersist(months, revenueTarget, forecastYearStr) {
    const fy =
        forecastYearStr != null && forecastYearStr !== ""
            ? Number.parseInt(String(forecastYearStr), 10)
            : NaN;
    if (!Number.isFinite(fy)) return 0;
    return months.reduce((sum, m, idx) => {
        if (!isForecastYearMonthClosedForPersist(fy, idx)) return sum;
        return sum + monthlyTarget(revenueTarget, m);
    }, 0);
}

export function countClosedForecastMonths(months, forecastYearStr) {
    const fy =
        forecastYearStr != null && forecastYearStr !== ""
            ? Number.parseInt(String(forecastYearStr), 10)
            : NaN;
    if (!Number.isFinite(fy) || !Array.isArray(months)) return 0;
    return months.reduce(
        (count, _m, idx) => count + (isForecastYearMonthClosedForPersist(fy, idx) ? 1 : 0),
        0
    );
}

export function readForecastTableSnapshot(planningRow, forecastYearStr) {
    const byYear = normalizePlanningData(planningRow?.data);
    const bucket = byYear[String(forecastYearStr)]?.table;
    if (!bucket || typeof bucket !== "object") return null;
    return {
        channels: Array.isArray(bucket.channels) ? structuredClone(bucket.channels) : [],
        categories: Array.isArray(bucket.categories) ? structuredClone(bucket.categories) : [],
        subCategories: Array.isArray(bucket.subCategories) ? structuredClone(bucket.subCategories) : [],
        months: Array.isArray(bucket.months) ? structuredClone(bucket.months) : [],
    };
}

export function channelLabelFromForecastApiKey(key) {
    if (key === "shopify") return "Shopify";
    if (key === "amazon") return "Amazon";
    const s = String(key || "").trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

export function lastClosedDateLabelForForecastYear(forecastYear) {
    if (forecastYear == null || forecastYear === "") return "—";
    const now = new Date();
    const currentYear = now.getFullYear();
    const fy = Number(forecastYear);
    if (!Number.isFinite(fy)) return "—";
    if (fy === currentYear) {
        const lastMonthDate = new Date(currentYear, now.getMonth(), 0);
        return lastMonthDate.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    }
    const lastDayOfYear = new Date(fy, 12, 0);
    return lastDayOfYear.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

/** Refresh category/subcategory/month actuals from live forecast API while preserving user % overrides. */
export function rebuildForecastTableSnapshotActualsFromApi(api, priorTable, forecastYearStr) {
    const prior = priorTable && typeof priorTable === "object" ? priorTable : {};
    const lcd = lastClosedDateLabelForForecastYear(forecastYearStr);

    const pctByChannel = new Map((prior.channels || []).map((row) => [row.channel, row.pct]));
    const channels = (api.channels || []).map((apiRow) => {
        const label = channelLabelFromForecastApiKey(apiRow.key);
        return {
            channel: label,
            pct: pctByChannel.has(label) ? pctByChannel.get(label) : null,
            channelKey: apiRow.key,
        };
    });

    const pctByCategory = new Map((prior.categories || []).map((row) => [row.category, row.pct]));
    const categories = (api.categories || []).map((freshCat) => ({
        category: freshCat.categoryName,
        categoryId: freshCat.categoryId ?? null,
        pct: pctByCategory.has(freshCat.categoryName) ? pctByCategory.get(freshCat.categoryName) : null,
        actuals_2025: freshCat.actuals,
        ytd_actuals:
            freshCat.ytd_actuals != null && Number.isFinite(Number(freshCat.ytd_actuals))
                ? Number(freshCat.ytd_actuals)
                : 0,
        last_closed_date:
            (prior.categories || []).find((row) => row.category === freshCat.categoryName)
                ?.last_closed_date ?? lcd,
        ltmPercentage:
            freshCat.ltmPercentage != null && Number.isFinite(Number(freshCat.ltmPercentage))
                ? Number(freshCat.ltmPercentage)
                : null,
    }));

    const pctBySubcategoryId = new Map(
        (prior.subCategories || []).map((row) => [String(row.id), row.pct_of_channel])
    );
    const subCategories = (api.subcategories || []).map((apiRow) => {
        const id = String(apiRow.subcategoryId);
        return {
            id,
            subcategoryId: apiRow.subcategoryId,
            sub_category: apiRow.subcategoryName,
            category: apiRow.categoryName ?? "",
            categoryId: apiRow.categoryId ?? null,
            pct_of_channel: pctBySubcategoryId.has(id) ? pctBySubcategoryId.get(id) : null,
            actuals_2025: apiRow.actuals,
            ytd_actuals:
                apiRow.ytd_actuals != null && Number.isFinite(Number(apiRow.ytd_actuals))
                    ? Number(apiRow.ytd_actuals)
                    : 0,
            last_closed_date:
                (prior.subCategories || []).find((row) => String(row.id) === id)?.last_closed_date ?? lcd,
            ltmPercentage:
                apiRow.ltmPercentage != null && Number.isFinite(Number(apiRow.ltmPercentage))
                    ? Number(apiRow.ltmPercentage)
                    : null,
        };
    });

    const overrideByMonth = new Map((prior.months || []).map((row) => [row.month, row.override_pct]));
    const months = (api.months || []).map((apiRow) => ({
        month: apiRow.key,
        historical_pct:
            apiRow.historicalPercentage != null && Number.isFinite(Number(apiRow.historicalPercentage))
                ? Number(apiRow.historicalPercentage) / 100
                : null,
        override_pct: overrideByMonth.has(apiRow.key) ? overrideByMonth.get(apiRow.key) : null,
        actual_pct:
            apiRow.actualPercentage != null && Number.isFinite(Number(apiRow.actualPercentage))
                ? Number(apiRow.actualPercentage) / 100
                : null,
        actual_revenue:
            apiRow.actualRevenue != null && Number.isFinite(Number(apiRow.actualRevenue))
                ? Number(apiRow.actualRevenue)
                : null,
    }));

    return { channels, categories, subCategories, months };
}

/** `pct_of_channel` on subcategory rows is share within the parent category (0–1); siblings sum to 1. */
export function categoryPctForName(categories, categoryName) {
    return categories.find((row) => row.category === categoryName)?.pct ?? 0;
}

export function sumSubcategoryPctWithinCategory(subCategories, categoryName) {
    return subCategories
        .filter((row) => row.category === categoryName)
        .reduce((sum, row) => sum + (row.pct_of_channel ?? 0), 0);
}

/** Derived global channel share: category % × within-category %. */
export function subcategoryPctOfChannel(categories, subCategoryRow) {
    const catPct = categoryPctForName(categories, subCategoryRow.category);
    return catPct * (subCategoryRow.pct_of_channel ?? 0);
}

export function computeSubcategoryYtdForecast({
    ytdForecastTotal,
    categories,
    subCategoryRow,
}) {
    const catPct = categoryPctForName(categories, subCategoryRow.category);
    return ytdForecastTotal * catPct * (subCategoryRow.pct_of_channel ?? 0);
}

export function enrichForecastTableSnapshotForDataColumn({
    channels,
    categories,
    subCategories,
    months,
    revenueTarget,
    forecastYearStr,
}) {
    const ytdForecastTotal = computeYtdForecastTotalForPersist(months, revenueTarget, forecastYearStr);
    const fyParsed =
        forecastYearStr != null && forecastYearStr !== ""
            ? Number.parseInt(String(forecastYearStr), 10)
            : NaN;

    const enrichedChannels = channels.map((ch) => ({
        ...ch,
        revenue_target: channelTarget(revenueTarget, ch.pct ?? 0),
    }));

    const enrichedCategories = categories.map((cat) => {
        const pct = cat.pct ?? 0;
        const revenue_target = categoryTarget(revenueTarget, pct);
        const ytd_forecast = Math.round(ytdForecastTotal * pct);
        return {
            ...cat,
            revenue_target,
            delta_vs_2025: revenue_target - (cat.actuals_2025 ?? 0),
            ytd_forecast,
            ytd_delta: (cat.ytd_actuals ?? 0) - ytd_forecast,
        };
    });

    const enrichedSubCategories = subCategories.map((sc) => {
        const poc = sc.pct_of_channel ?? 0;
        const catPct = categories.find((c) => c.category === sc.category)?.pct ?? 0;
        const categoryRevTarget = categoryTarget(revenueTarget, catPct);
        const revenue_target = subCatTarget(categoryRevTarget, poc);
        const ytd_forecast = Math.round(ytdForecastTotal * catPct * poc);
        return {
            ...sc,
            revenue_target,
            delta_vs_2025: revenue_target - (sc.actuals_2025 ?? 0),
            ytd_forecast,
            ytd_delta: (sc.ytd_actuals ?? 0) - ytd_forecast,
        };
    });

    const enrichedMonths = months.map((m, idx) => {
        const final_pct = finalMonthPct(m);
        const revenue_target = monthlyTarget(revenueTarget, m);
        const closed =
            Number.isFinite(fyParsed) && isForecastYearMonthClosedForPersist(fyParsed, idx);
        const actualRev = m.actual_revenue;
        const revenue_delta =
            closed && actualRev != null && actualRev !== undefined
                ? actualRev - revenue_target
                : null;
        return {
            ...m,
            final_pct,
            revenue_target,
            month_closed: closed,
            revenue_delta,
        };
    });

    return {
        channels: enrichedChannels,
        categories: enrichedCategories,
        subCategories: enrichedSubCategories,
        months: enrichedMonths,
    };
}

export function rebalanceCategoryPctRows(categories, category, pct) {
    const nextPct = Math.min(Math.max(pct, 0), 1);
    const totalOther = categories.reduce(
        (sum, row) => sum + (row.category === category ? 0 : (row.pct ?? 0)),
        0
    );
    const remaining = Math.max(0, 1 - nextPct);
    return categories.map((row) => {
        const currentPct = row.pct ?? 0;
        const adjustedPct =
            row.category === category
                ? nextPct
                : totalOther <= 0
                  ? 0
                  : (currentPct / totalOther) * remaining;
        return { ...row, pct: adjustedPct };
    });
}

export function rebalanceSubCategoryPctRows(subCategories, categories, id, pct) {
    const targetRow = subCategories.find((row) => String(row.id) === String(id));
    if (!targetRow) return subCategories;

    const nextWithinCategoryPct = Math.min(Math.max(pct, 0), 1);
    const siblingTotal = subCategories.reduce(
        (sum, row) =>
            sum +
            (row.category === targetRow.category && String(row.id) !== String(id)
                ? row.pct_of_channel ?? 0
                : 0),
        0
    );
    const remaining = Math.max(0, 1 - nextWithinCategoryPct);

    return subCategories.map((row) => {
        if (row.category !== targetRow.category) return row;
        const currentPct = row.pct_of_channel ?? 0;
        const adjustedPct =
            String(row.id) === String(id)
                ? nextWithinCategoryPct
                : siblingTotal <= 0
                  ? 0
                  : (currentPct / siblingTotal) * remaining;
        return { ...row, pct_of_channel: adjustedPct };
    });
}

export function buildForecastYearDataPatch({
    existingData,
    forecastYearStr,
    tableSnapshot,
    apiJson,
    revenueTarget,
}) {
    const byYear = normalizePlanningData(existingData);
    const yk = String(forecastYearStr);
    const enriched = enrichForecastTableSnapshotForDataColumn({
        channels: tableSnapshot.channels,
        categories: tableSnapshot.categories,
        subCategories: tableSnapshot.subCategories,
        months: tableSnapshot.months,
        revenueTarget,
        forecastYearStr,
    });

    return {
        ...byYear,
        [yk]: {
            api: structuredClone(apiJson),
            table: structuredClone(enriched),
        },
    };
}
